import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import SelectHoi from 'components/SelectHoi';
import { getLiteral } from 'utils/getLiteral';
import { isEqual } from 'utils/objects';

// INFO:
// This SelectCustom field is for those select fields that despite sharing schema with other fields
// taking their values from an specific place in the store (entityCrud, entityFilters...) have their
// data in another place in the store

const getDataWithDataPath = (data, dataPath, count = 0) => {
    if (data && dataPath[count]) {
        const newData = data[dataPath[count]];
        if (dataPath[count + 1]) return getDataWithDataPath(newData, dataPath, count + 1);
        else return newData;
    } else {
        return data;
    }
};

const propTypes = {
    autoFocus: PropTypes.bool,
    dataPath: PropTypes.array,
    disabled: PropTypes.bool,
    fieldId: PropTypes.string,
    groupedOptions: PropTypes.array,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    isClearable: PropTypes.bool,
    onChange: PropTypes.func,
    options: PropTypes.array,
    placeholderReadOnly: PropTypes.string,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    value: PropTypes.any,
};

const SelectCustom = memo(
    ({
        dataPath,
        disabled,
        error,
        fieldId,
        groupedOptions,
        hidden,
        hint,
        isClearable = false,
        onChange,
        options = [],
        placeholderReadOnly,
        readOnly,
        shouldRenderField,
        value,
        ...props
    }) => {
        const [innerSelected, setInnerSelected] = useState(null);
        const oldSelectedRef = useRef(null);

        // Get custom options from Redux store using dataPath
        const customOptions = useSelector((state) => {
            if (dataPath && dataPath.length > 0) {
                return getDataWithDataPath(state, dataPath) || [];
            }
            return [];
        });

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const finalOptions = useMemo(() => {
            let newOptions = options;
            if (customOptions.length > 0) newOptions = customOptions;
            if (groupedOptions) newOptions = groupedOptions;
            return newOptions;
        }, [customOptions, groupedOptions, options]);

        // ========================================
        // EFFECTS
        // ========================================
        // Initialize or update innerSelected when value or options change
        useEffect(() => {
            // Get the primitive value for comparison (handle both object and primitive)
            const valueToCompare = value?.value ?? value;

            // Skip if value hasn't changed (comparing with ref)
            if (oldSelectedRef.current?.value === valueToCompare) return;

            if (valueToCompare && finalOptions.length > 0) {
                const selected =
                    finalOptions.find((i) => String(i.value) === String(valueToCompare)) || null;
                // If value exists but not found in options, keep the current value object
                if (!selected && value && typeof value === 'object') {
                    oldSelectedRef.current = value;
                    setInnerSelected(value);
                } else {
                    oldSelectedRef.current = selected;
                    setInnerSelected(selected);
                }
            } else if (!valueToCompare) {
                oldSelectedRef.current = null;
                setInnerSelected(null);
            }
        }, [finalOptions, value]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleOnChange = useCallback(
            (item) => {
                if (dataPath) {
                    setInnerSelected(item);
                    oldSelectedRef.current = item;
                    onChange && onChange(item);
                }
            },
            [setInnerSelected, onChange, dataPath],
        );

        // ========================================
        // RENDER
        // ========================================
        if (hidden) return null;
        if (shouldRenderField && !shouldRenderField(fieldId)) return null;

        return (
            <SelectHoi
                error={!!error}
                isClearable={isClearable}
                isReadOnly={readOnly || disabled}
                onChange={handleOnChange}
                options={finalOptions}
                placeholder={hint || getLiteral('label_selectone')}
                placeholderReadOnly={placeholderReadOnly || hint || getLiteral('label_selectone')}
                value={innerSelected}
                isFullWidth
                {...props}
            />
        );
    },
);

SelectCustom.displayName = 'SelectCustom';

SelectCustom.propTypes = propTypes;

export default SelectCustom;
